'use client';

import { useCallback, useContext, useRef, useState, type ChangeEvent, type RefObject } from 'react';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { useQueryClient } from '@tanstack/react-query';
import { ToastContext } from '@/app/contexts';
import { ToastLevel } from '@/components/ui/toast';
import { getGatewaySession } from '@/lib/gateway-fetch';
import {
  EdgeLambdaApiError,
  createManualFolder,
  purgeManualCaseFiles,
  uploadManualCaseFile,
} from '@/shared/cors-edge-lambda-api';
import { getCaseDisplayName } from '@/shared/functions';
import {
  fetchManualFolderChildBasenames,
  fetchManualFolderFileNames,
  isManualUploadFolder,
  manualUploadFileName,
  manualUploadPrefixForFolder,
  suggestManualUploadRenameBasename,
  validateManualUploadRenameBasename,
  validateNewFolderName,
} from '@/shared/case-file-management';
import { provenanceKeys } from '@/shared/cors-provenance-api';
import { formatUploadErrorDetail } from '@/shared/upload-error-detail';
import type { AdHocFileConflictModalProps } from '@/components/ad-hoc-file-conflict-modal';

type ConflictResolution =
  | { type: 'replace' }
  | { type: 'rename'; fileName: string }
  | { type: 'skip' };

type PendingUpload = {
  file: File;
  fileName: string;
};

export type CaseFileManagementUploadApi = {
  fileInputRef: RefObject<HTMLInputElement>;
  openFilePicker: (folderTreePath: string) => void;
  handleFileInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  uploadFilesToFolder: (
    folderTreePath: string,
    files: FileList | File[],
    options?: { existingNames?: ReadonlySet<string>; reuseActiveFlow?: boolean },
  ) => Promise<boolean>;
  /** Create ``folderName`` under ``parentFolderTreePath`` and upload files into it. */
  uploadFilesToNewFolder: (
    parentFolderTreePath: string,
    folderName: string,
    files: FileList | File[],
  ) => Promise<boolean>;
  deleteManualFile: (manualRelativeName: string) => Promise<boolean>;
  /** Sync provenance case-root path used to resolve Manual folder paths in the tree. */
  setCaseRootPath: (path: string | null) => void;
  /** Sync the folder currently open in the Case Files browser (page-level drops). */
  setCurrentBrowsePath: (path: string | null) => void;
  currentBrowsePath: string | null;
  caseRootPath: string | null;
  isUploading: boolean;
  activeDropFolderPath: string | null;
  setActiveDropFolderPath: (path: string | null) => void;
  conflictModal: AdHocFileConflictModalProps;
};

type UseCaseFileManagementUploadOptions = {
  caseId: string | undefined;
  /**
   * S3 folder segment for the selected case. Main passes its own resolver:
   * this hook runs inside Main, above the auth and feature-flag providers,
   * so ``useAwaitedCaseFolderNameResolver`` would always return null and
   * block every upload.
   */
  resolveStorageCaseId: (caseId: string | null | undefined) => Promise<string | null>;
};

export function useCaseFileManagementUpload(
  options: UseCaseFileManagementUploadOptions,
): CaseFileManagementUploadApi {
  const t = useTimePilotT();
  const { caseId, resolveStorageCaseId: resolveFolderSegment } = options;
  const { setToast, setToastOpen } = useContext(ToastContext);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingFolderPathRef = useRef<string>('');
  const caseRootPathRef = useRef<string | null>(null);
  const uploadFlowActiveRef = useRef(false);

  const [isUploading, setIsUploading] = useState(false);
  const [activeDropFolderPath, setActiveDropFolderPath] = useState<string | null>(null);
  const [caseRootPath, setCaseRootPathState] = useState<string | null>(null);
  const [currentBrowsePath, setCurrentBrowsePathState] = useState<string | null>(null);

  const conflictResolverRef = useRef<((resolution: ConflictResolution) => void) | null>(null);
  const [conflictState, setConflictState] = useState<{
    file: File;
    fileName: string;
    uploadPrefix: string;
    canReplace: boolean;
    renameValue: string;
    existingNames: Set<string>;
    folderLabel: string;
  } | null>(null);
  const [renameError, setRenameError] = useState<string | null>(null);

  const setCaseRootPath = useCallback((path: string | null) => {
    const next = path?.trim() || null;
    caseRootPathRef.current = next;
    setCaseRootPathState(next);
  }, []);

  const setCurrentBrowsePath = useCallback((path: string | null) => {
    setCurrentBrowsePathState(path?.trim() || null);
  }, []);

  const showToast = useCallback(
    (title: string, description: string, level: ToastLevel, duration = 4000) => {
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration,
        title,
        description,
        level,
      });
    },
    [setToast, setToastOpen],
  );

  // S3 keys use the list's folder_segment, not the dropdown's display name.
  // A miss must not fall back to the name: that writes the wrong prefix.
  const resolveStorageCaseId = useCallback(async (): Promise<string | null> => {
    const selected = caseId?.trim();
    if (!selected) return null;
    const segment = (await resolveFolderSegment(selected))?.trim();
    return segment || null;
  }, [caseId, resolveFolderSegment]);

  const finishConflict = useCallback((resolution: ConflictResolution) => {
    conflictResolverRef.current?.(resolution);
    conflictResolverRef.current = null;
    setConflictState(null);
    setRenameError(null);
  }, []);

  const promptConflict = useCallback(
    (
      file: File,
      fileName: string,
      canReplace: boolean,
      existingNames: Set<string>,
      folderLabel: string,
      uploadPrefix: string,
    ): Promise<ConflictResolution> =>
      new Promise((resolve) => {
        conflictResolverRef.current = resolve;
        setRenameError(null);
        const takenNames = new Set([...existingNames, fileName]);
        setConflictState({
          file,
          fileName,
          uploadPrefix,
          canReplace,
          renameValue: suggestManualUploadRenameBasename(fileName, takenNames),
          existingNames: takenNames,
          folderLabel,
        });
      }),
    [],
  );

  const resolveUploadQueue = useCallback(
    async (
      files: File[],
      existingNames: Set<string>,
      folderLabel: string,
      uploadPrefix: string,
    ): Promise<PendingUpload[]> => {
      const resolved: PendingUpload[] = [];
      const namesInBatch = new Set<string>();

      for (const file of files) {
        let targetName = manualUploadFileName(uploadPrefix, file.name.trim());
        if (!targetName) continue;

        let settled = false;
        while (!settled) {
          const existsOnServer = existingNames.has(targetName);
          const duplicateInBatch = namesInBatch.has(targetName);

          if (!existsOnServer && !duplicateInBatch) {
            resolved.push({ file, fileName: targetName });
            namesInBatch.add(targetName);
            settled = true;
            continue;
          }

          const resolution = await promptConflict(
            file,
            targetName,
            existsOnServer,
            new Set([...existingNames, ...namesInBatch]),
            folderLabel,
            uploadPrefix,
          );

          if (resolution.type === 'skip') {
            settled = true;
            continue;
          }

          if (resolution.type === 'replace') {
            resolved.push({ file, fileName: targetName });
            namesInBatch.add(targetName);
            settled = true;
            continue;
          }

          targetName = resolution.fileName.trim();
        }
      }

      return resolved;
    },
    [promptConflict],
  );

  const uploadResolvedFiles = useCallback(
    async (
      queue: PendingUpload[],
      session: { cellId: string; accessToken: string },
      resolvedCaseId: string,
    ) => {
      const failed: string[] = [];
      let firstErrorDetail: string | null = null;

      for (const { file, fileName } of queue) {
        try {
          await uploadManualCaseFile({
            cellId: session.cellId,
            accessToken: session.accessToken,
            file,
            fileName,
            caseId: resolvedCaseId,
          });
        } catch (error) {
          failed.push(fileName);
          if (!firstErrorDetail && error instanceof EdgeLambdaApiError) {
            firstErrorDetail = formatUploadErrorDetail(error) ?? error.message;
          }
        }
      }

      return { failed, firstErrorDetail, total: queue.length };
    },
    [],
  );

  const refreshProvenanceAfterMutation = useCallback(async () => {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: provenanceKeys.tree() }),
      queryClient.refetchQueries({ queryKey: provenanceKeys.files() }),
    ]);
  }, [queryClient]);

  const uploadFilesToFolder = useCallback(
    async (
      folderTreePath: string,
      files: FileList | File[],
      options?: { existingNames?: ReadonlySet<string>; reuseActiveFlow?: boolean },
    ): Promise<boolean> => {
      const reuseActiveFlow = options?.reuseActiveFlow === true;
      const resolvedCaseId = caseId?.trim();
      if (!resolvedCaseId || (!reuseActiveFlow && uploadFlowActiveRef.current)) return false;
      if (!isManualUploadFolder(folderTreePath, resolvedCaseId, caseRootPathRef.current)) {
        showToast(t('upload.failed'), t('upload.manualOnly'), ToastLevel.ERROR);
        return false;
      }

      if (!reuseActiveFlow) uploadFlowActiveRef.current = true;
      try {
        const fileArray = Array.from(files).filter((f) => f.size > 0 && f.name.trim() !== '');
        if (!fileArray.length) return false;

        const session = await getGatewaySession();
        if (!session) {
          showToast(t('upload.failed'), t('upload.noSession'), ToastLevel.ERROR);
          return false;
        }

        let existingNames: Set<string>;
        if (options?.existingNames) {
          existingNames = new Set(options.existingNames);
        } else {
          try {
            existingNames = await fetchManualFolderFileNames({
              cellId: session.cellId,
              accessToken: session.accessToken,
              caseName: resolvedCaseId,
              folderTreePath,
              caseRootPath: caseRootPathRef.current,
            });
          } catch (error) {
            showToast(
              t('upload.failed'),
              t('upload.checkFilesFailed', { detail: (error as Error).message }),
              ToastLevel.ERROR,
            );
            return false;
          }
        }

        const folderLabel = folderTreePath.split('/').pop() ?? 'folder';
        const uploadPrefixResolved = manualUploadPrefixForFolder(
          folderTreePath,
          resolvedCaseId,
          caseRootPathRef.current,
        );

        const queue = await resolveUploadQueue(
          fileArray,
          existingNames,
          folderLabel,
          uploadPrefixResolved,
        );
        if (!queue.length) return false;

        const displayName = (fileName: string) => fileName.split('/').pop() ?? fileName;
        showToast(
          t('upload.started'),
          queue.length === 1
            ? t('upload.startedOne', { name: displayName(queue[0]!.fileName) })
            : t('upload.startedMany', { count: queue.length }),
          ToastLevel.INFO,
          12000,
        );

        const storageCaseId = await resolveStorageCaseId();
        if (!storageCaseId) {
          showToast(
            t('upload.failed'),
            t('upload.caseFolderFailed'),
            ToastLevel.ERROR,
          );
          return false;
        }

        if (!reuseActiveFlow) setIsUploading(true);
        const { failed, firstErrorDetail, total } = await uploadResolvedFiles(queue, session, storageCaseId);
        if (!reuseActiveFlow) setIsUploading(false);

        if (failed.length === 0) {
          showToast(
            t('upload.received'),
            total === 1
              ? t('upload.receivedOne', { name: displayName(queue[0]!.fileName), caseName: getCaseDisplayName(resolvedCaseId) })
              : t('upload.receivedMany', { count: total, caseName: getCaseDisplayName(resolvedCaseId) }),
            ToastLevel.SUCCESS,
            12000,
          );
          await refreshProvenanceAfterMutation();
          return true;
        }

        if (failed.length === total) {
          const description =
            total === 1
              ? firstErrorDetail
                ? t('upload.couldNotUploadOneDetail', { name: failed[0], detail: firstErrorDetail })
                : t('upload.couldNotUploadOne', { name: failed[0] })
              : firstErrorDetail
                ? t('upload.couldNotUploadManyDetail', { detail: firstErrorDetail })
                : t('upload.couldNotUploadMany');
          showToast(t('upload.failed'), description, ToastLevel.ERROR);
          return false;
        }

        showToast(
          t('upload.partial'),
          t('upload.partialDescription', {
            uploaded: total - failed.length,
            total,
            failed: failed.join(', '),
          }),
          ToastLevel.WARNING,
          12000,
        );
        await refreshProvenanceAfterMutation();
        return true;
      } finally {
        if (!reuseActiveFlow) {
          uploadFlowActiveRef.current = false;
          setActiveDropFolderPath(null);
        }
      }
    },
    [caseId, refreshProvenanceAfterMutation, resolveStorageCaseId, resolveUploadQueue, showToast, t, uploadResolvedFiles],
  );

  const openFilePicker = useCallback(
    (folderTreePath: string) => {
      if (uploadFlowActiveRef.current || isUploading || conflictState) return;
      pendingFolderPathRef.current = folderTreePath;
      fileInputRef.current?.click();
    },
    [conflictState, isUploading],
  );

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files;
      const folderPath = pendingFolderPathRef.current;
      if (selected?.length && folderPath && !uploadFlowActiveRef.current) {
        void uploadFilesToFolder(folderPath, selected);
      }
      event.target.value = '';
      pendingFolderPathRef.current = '';
    },
    [uploadFilesToFolder],
  );

  const uploadFilesToNewFolder = useCallback(
    async (
      parentFolderTreePath: string,
      folderName: string,
      files: FileList | File[],
    ): Promise<boolean> => {
      const resolvedCaseId = caseId?.trim();
      const trimmedName = folderName.trim();
      const fileArray = Array.from(files).filter((f) => f.size > 0 && f.name.trim() !== '');
      if (!resolvedCaseId || !trimmedName || fileArray.length === 0 || uploadFlowActiveRef.current) {
        return false;
      }

      uploadFlowActiveRef.current = true;
      setIsUploading(true);
      try {
        if (!isManualUploadFolder(parentFolderTreePath, resolvedCaseId, caseRootPathRef.current)) {
          showToast(
            t('upload.failed'),
            t('upload.manualFoldersOnly'),
            ToastLevel.ERROR,
          );
          return false;
        }

        const newFolderPath = `${parentFolderTreePath.replace(/\/+$/, '')}/${trimmedName}`;

        const session = await getGatewaySession();
        if (!session) {
          showToast(
            t('upload.failed'),
            t('upload.noSession'),
            ToastLevel.ERROR,
          );
          return false;
        }

        let existingNames: Set<string>;
        try {
          existingNames = await fetchManualFolderChildBasenames({
            cellId: session.cellId,
            accessToken: session.accessToken,
            folderTreePath: parentFolderTreePath,
          });
        } catch (error) {
          showToast(
            t('upload.failed'),
            t('upload.checkFoldersFailed', { detail: (error as Error).message }),
            ToastLevel.ERROR,
          );
          return false;
        }

        const validationError = validateNewFolderName(trimmedName, existingNames);
        if (validationError) {
          showToast(t('upload.failed'), validationError, ToastLevel.ERROR);
          return false;
        }

        const relativeName = manualUploadFileName(
          manualUploadPrefixForFolder(parentFolderTreePath, resolvedCaseId, caseRootPathRef.current),
          trimmedName,
        );

        const storageCaseId = await resolveStorageCaseId();
        if (!storageCaseId) {
          showToast(
            t('upload.failed'),
            t('upload.caseFolderFailed'),
            ToastLevel.ERROR,
          );
          return false;
        }

        try {
          await createManualFolder({
            cellId: session.cellId,
            accessToken: session.accessToken,
            caseId: storageCaseId,
            folderName: relativeName,
          });
        } catch (error) {
          const detail =
            error instanceof EdgeLambdaApiError
              ? formatUploadErrorDetail(error) ?? error.message
              : null;
          showToast(
            t('upload.failed'),
            detail ?? t('upload.couldNotCreateFolder', { name: trimmedName }),
            ToastLevel.ERROR,
          );
          return false;
        }

        return await uploadFilesToFolder(newFolderPath, fileArray, {
          existingNames: new Set(),
          reuseActiveFlow: true,
        });
      } finally {
        uploadFlowActiveRef.current = false;
        setIsUploading(false);
      }
    },
    [caseId, resolveStorageCaseId, showToast, t, uploadFilesToFolder],
  );

  const deleteManualFile = useCallback(
    async (manualRelativeName: string): Promise<boolean> => {
      const resolvedCaseId = caseId?.trim();
      const trimmed = manualRelativeName.trim();
      if (!resolvedCaseId || !trimmed) return false;

      if (
        !window.confirm(
          t('upload.confirmDelete', { name: trimmed.split('/').pop(), caseName: getCaseDisplayName(resolvedCaseId) }),
        )
      ) {
        return false;
      }

      const session = await getGatewaySession();
      if (!session) {
        showToast(t('upload.deleteFailed'), t('upload.noSession'), ToastLevel.ERROR);
        return false;
      }

      const storageCaseId = await resolveStorageCaseId();
      if (!storageCaseId) {
        showToast(
          t('upload.deleteFailed'),
          t('upload.caseFolderFailed'),
          ToastLevel.ERROR,
        );
        return false;
      }

      setIsUploading(true);
      try {
        await purgeManualCaseFiles({
          cellId: session.cellId,
          accessToken: session.accessToken,
          caseId: storageCaseId,
          fileNames: [trimmed],
        });
        showToast(
          t('upload.fileDeleted'),
          t('upload.fileDeletedDescription', { name: trimmed.split('/').pop(), caseName: getCaseDisplayName(resolvedCaseId) }),
          ToastLevel.SUCCESS,
        );
        await refreshProvenanceAfterMutation();
        return true;
      } catch (error) {
        const detail =
          error instanceof EdgeLambdaApiError && error.detail?.trim()
            ? error.detail.trim()
            : null;
        showToast(t('upload.deleteFailed'), detail ?? t('upload.couldNotDelete', { name: trimmed.split('/').pop() }), ToastLevel.ERROR);
        return false;
      } finally {
        setIsUploading(false);
      }
    },
    [caseId, refreshProvenanceAfterMutation, resolveStorageCaseId, showToast, t],
  );

  const handleConflictReplace = useCallback(() => {
    finishConflict({ type: 'replace' });
  }, [finishConflict]);

  const handleConflictCancel = useCallback(() => {
    finishConflict({ type: 'skip' });
  }, [finishConflict]);

  const handleConflictRename = useCallback(() => {
    if (!conflictState) return;
    const trimmedBasename = conflictState.renameValue.trim();
    const validationError = validateManualUploadRenameBasename(
      trimmedBasename,
      conflictState.uploadPrefix,
      conflictState.fileName,
      conflictState.existingNames,
    );
    if (validationError) {
      setRenameError(validationError);
      return;
    }
    finishConflict({
      type: 'rename',
      fileName: manualUploadFileName(conflictState.uploadPrefix, trimmedBasename),
    });
  }, [conflictState, finishConflict]);

  const handleRenameValueChange = useCallback((value: string) => {
    setRenameError(null);
    setConflictState((prev) => (prev ? { ...prev, renameValue: value } : prev));
  }, []);

  const conflictModal: AdHocFileConflictModalProps = {
    isOpen: conflictState !== null,
    fileName: conflictState?.fileName ?? '',
    canReplace: conflictState?.canReplace ?? false,
    renameValue: conflictState?.renameValue ?? '',
    renameError,
    folderLabel: conflictState?.folderLabel,
    caseDisplayName: caseId ? getCaseDisplayName(caseId) : undefined,
    onRenameValueChange: handleRenameValueChange,
    onReplace: handleConflictReplace,
    onRename: handleConflictRename,
    onCancel: handleConflictCancel,
  };

  return {
    fileInputRef,
    openFilePicker,
    handleFileInputChange,
    uploadFilesToFolder,
    uploadFilesToNewFolder,
    deleteManualFile,
    setCaseRootPath,
    setCurrentBrowsePath,
    currentBrowsePath,
    caseRootPath,
    isUploading,
    activeDropFolderPath,
    setActiveDropFolderPath,
    conflictModal,
  };
}
